import React from 'react';
import { Link } from 'react-router-dom';

const GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=com.saverly.app&hl=en_US';

const DownloadPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-saverly-primary/10 to-saverly-deep/10">
      {/* Hero Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-saverly-dark font-poppins mb-6">
            Download Saverly
            <span className="block text-saverly-primary mt-2">Start Saving Today</span>
          </h1>
          <p className="text-xl text-saverly-gray font-inter max-w-2xl mx-auto mb-12">
            Build a weekly meal plan from local store sales, auto-generate your shopping list, and save more on every trip.
          </p>

          {/* App Store Badges */}
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-16">
            {/* App Store Button */}
            <a
              href="https://apps.apple.com/us/app/saverly-kroger-meals/id6756591268"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block transition-transform duration-200 hover:scale-105"
            >
              <img
                src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83"
                alt="Download on the App Store"
                className="h-14"
              />
            </a>

            {/* Google Play Button */}
            <a
              href={GOOGLE_PLAY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block transition-transform duration-200 hover:scale-105"
            >
              <img
                src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                alt="Get it on Google Play"
                className="h-20"
              />
            </a>
          </div>
          
          <p className="text-saverly-gray text-center mb-12 font-inter">
            Available on iOS and Android
          </p>
        </div>
      </section>

      {/* Quick highlights for ad traffic */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-saverly-dark font-poppins mb-4">
              Why people use Saverly
            </h2>
            <p className="text-xl text-saverly-gray font-inter">
              Quick, simple, and built around real store deals
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="text-center">
              <div className="w-16 h-16 bg-saverly-primary/10 rounded-2xl flex items-center justify-center mb-4 mx-auto">
                <svg className="w-8 h-8 text-saverly-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-saverly-dark font-poppins mb-2">Sales-Powered Meal Planning</h3>
              <p className="text-saverly-gray font-inter">Get complete meal ideas based on what is on sale nearby.</p>
            </div>

            {/* Feature 2 */}
            <div className="text-center">
              <div className="w-16 h-16 bg-saverly-accent/10 rounded-2xl flex items-center justify-center mb-4 mx-auto">
                <svg className="w-8 h-8 text-saverly-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-saverly-dark font-poppins mb-2">Smart Shopping</h3>
              <p className="text-saverly-gray font-inter">Auto-build a list and keep everything organized by aisle.</p>
            </div>

            {/* Feature 3 */}
            <div className="text-center">
              <div className="w-16 h-16 bg-saverly-deep/10 rounded-2xl flex items-center justify-center mb-4 mx-auto">
                <svg className="w-8 h-8 text-saverly-deep" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-saverly-dark font-poppins mb-2">Product Details</h3>
              <p className="text-saverly-gray font-inter">Review matched products, pricing, and nutrition before you shop.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How to download */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-saverly-dark font-poppins mb-6">
            How to get started
          </h2>
          <div className="grid sm:grid-cols-3 gap-4 text-left mb-10">
            <div className="bg-white rounded-xl p-4 border border-gray-200">
              <p className="text-sm font-semibold text-saverly-primary mb-1">Step 1</p>
              <p className="text-saverly-gray font-inter">Tap your app store button above.</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-gray-200">
              <p className="text-sm font-semibold text-saverly-primary mb-1">Step 2</p>
              <p className="text-saverly-gray font-inter">Install Saverly and select your local store.</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-gray-200">
              <p className="text-sm font-semibold text-saverly-primary mb-1">Step 3</p>
              <p className="text-saverly-gray font-inter">Build your week and generate your list in minutes.</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/how-it-works"
              className="inline-block bg-white hover:bg-gray-100 text-saverly-primary font-semibold px-8 py-3 rounded-xl border border-saverly-primary/30 transition-all duration-200"
            >
              See how it works
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DownloadPage;

